import React, { useEffect, useRef, useState } from 'react';
import { useKeyboardShortcut } from '@/utils/keyboard';
import { FileVideoIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { KeyHint } from '@/components/KeyHint';

const VIDEO_EXTENSIONS = [
    '.mp4',
    '.m4v',
    '.mov',
    '.avi',
    '.mkv',
    '.webm',
    '.wmv',
    '.flv',
    '.ts',
    '.mts',
    '.m2ts',
    '.m2t',
    '.vob',
    '.evo',
    '.vro',
    '.mpeg',
    '.mpg',
    '.mpe',
    '.mpv',
    '.m1v',
    '.m2v',
    '.3gp',
    '.3g2',
    '.3mm',
    '.k3g',
    '.asf',
    '.divx',
    '.xvid',
    '.rm',
    '.rmvb',
    '.ogv',
    '.ogg',
    '.dv',
    '.mxf',
    '.gxf',
    '.nut',
    '.f4v',
    '.f4p',
    '.swf',
    '.mod',
    '.tod',
    '.wtv',
    '.dvr-ms',
    '.rec',
    '.ssif',
    '.bik',
    '.roq',
    '.nsv',
    '.amv',
    '.yuv',
    '.y4m',
    '.cpk',
    '.pss',
    '.str',
    '.pmf',
    '.svi',
    '.hevc',
    '.h264',
    '.h265',
    '.264',
    '.265',
    '.avs',
    '.avs2',
    '.drc',
    '.ivf',
    '.dav',
    '.g64',
    '.g64x',
    '.ave',
    '.bvr',
    '.nov',
    '.hik',
    '.nvr',
    '.dvr',
    '.sec',
    '.pef',
    '.cmr',
    '.mjp',
    '.mjpg',
    '.mjpeg',
    '.media',
    '.arf',
    '.wrf',
    '.trec',
    '.camrec',
    '.qt',
    '.fli',
    '.flc',
    '.vid',
    '.viv',
    '.bin',
    '.dat',
    '.vp6',
    '.vp8',
    '.vp9',
    '.av1',
    '.cine',
    '.braw',
    '.r3d',
    '.mj2',
    '.jpgv',
    '.smv',
    '.pva',
    '.tvs',
    '.pvr',
    '.seq',
    '.vdr',
    '.hdv',
    '.nsr',
    '.axv',
];

function isVideoFile(file: File): boolean {
    if (file.type.startsWith('video/')) {
        return true;
    }
    const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    return VIDEO_EXTENSIONS.includes(extension);
}

interface DropzoneProps {
    uploadText?: string;
    onFilesSelected?: (files: File[]) => void;
    className?: string;
}

export function Dropzone({
    uploadText,
    onFilesSelected,
    className,
}: DropzoneProps) {
    const { t } = useTranslation(I18nNamespace.COMMON);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dropzoneRef = useRef<HTMLDivElement>(null);

    const handleSelectFilesClick = () => {
        fileInputRef.current?.click();
    };

    useKeyboardShortcut(['u'], handleSelectFilesClick);

    useEffect(() => {
        const dropzone = dropzoneRef.current;
        if (!dropzone) return;

        const handleDragEnter = (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(true);
        };

        const handleDragLeave = (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();

            if (dropzone && !dropzone.contains(e.relatedTarget as Node)) {
                setIsDragging(false);
            }
        };

        const handleDragOver = (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.dataTransfer) {
                e.dataTransfer.dropEffect = 'copy';
            }
        };

        const handleDrop = (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);

            const files = Array.from(e.dataTransfer?.files || []);
            const validFiles = files.filter(isVideoFile);

            if (validFiles.length > 0 && onFilesSelected) {
                onFilesSelected(validFiles);
            }
        };

        dropzone.addEventListener('dragenter', handleDragEnter);
        dropzone.addEventListener('dragleave', handleDragLeave);
        dropzone.addEventListener('dragover', handleDragOver);
        dropzone.addEventListener('drop', handleDrop);

        return () => {
            dropzone.removeEventListener('dragenter', handleDragEnter);
            dropzone.removeEventListener('dragleave', handleDragLeave);
            dropzone.removeEventListener('dragover', handleDragOver);
            dropzone.removeEventListener('drop', handleDrop);
        };
    }, [onFilesSelected]);

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        const validFiles = files.filter(isVideoFile);

        if (validFiles.length > 0 && onFilesSelected) {
            onFilesSelected(validFiles);
        }

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className={className}>
            <div
                ref={dropzoneRef}
                className={`
                    h-full
                    bg-grey-650 border-2 border-dashed rounded-lg
                    transition-colors duration-200 cursor-copy
                    ${isDragging ? 'border-grey-300 bg-grey-600' : 'border-grey-400'}
                `}
            >
                <div className="h-full flex flex-col items-center justify-center p-12">
                    <FileVideoIcon className="w-5 h-5 text-grey-300 mb-4" />
                    <p className="text-white-100 text-lg-medium mb-2 text-center">
                        {uploadText ?? t('dropzone.dragAndDropVideos')}
                    </p>
                    <button
                        type="button"
                        onClick={handleSelectFilesClick}
                        className="flex items-center gap-2 px-4 py-2 bg-grey-650 border border-grey-400 hover:bg-grey-600/30 transition-colors rounded-lg text-sm text-grey-200 hover:text-white-100"
                    >
                        <span>{t('button.selectFiles')}</span>
                        <KeyHint>U</KeyHint>
                    </button>
                </div>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={`video/*,${VIDEO_EXTENSIONS.join(',')}`}
                onChange={handleFileInputChange}
                className="hidden"
            />
        </div>
    );
}
